import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { SplitText } from './motion';

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

const BASE_BUTTON =
  'sheen group relative inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 py-3 ' +
  'text-[0.95rem] font-semibold transition-[transform,box-shadow,background-color,border-color] ' +
  'duration-300 ease-[var(--ease-siv)] motion-reduce:transition-none ' +
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0';

/**
 * The blue→emerald gradient is taken straight from the mark. White label text
 * measures 5.3:1 or better against every stop, in both themes.
 */
const PRIMARY =
  `${BASE_BUTTON} bg-brand text-white ` +
  'shadow-[0_10px_34px_-12px_var(--brand-shadow)] ' +
  'hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_18px_44px_-12px_var(--brand-shadow)] ' +
  'active:not-disabled:translate-y-0';

const SECONDARY =
  `${BASE_BUTTON} border border-line-strong bg-surface text-text ` +
  'hover:not-disabled:-translate-y-0.5 hover:not-disabled:border-blue hover:not-disabled:bg-surface-2';

export type ButtonVariant = 'primary' | 'secondary';

const variantClass = (v: ButtonVariant) => (v === 'primary' ? PRIMARY : SECONDARY);

/** Pulls the element gently toward the pointer. Fine-pointer devices only. */
function useMagnetic<T extends HTMLElement>(strength = 0.28) {
  const ref = useRef<T>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node || reduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        node.style.translate = `${dx * strength}px ${dy * strength}px`;
      });
    };
    const reset = () => {
      cancelAnimationFrame(frame);
      node.style.translate = '0px 0px';
    };

    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', reset);
    return () => {
      cancelAnimationFrame(frame);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', reset);
    };
  }, [reduced, strength]);

  return ref;
}

export function ButtonLink({
  to,
  variant = 'primary',
  className = '',
  children,
}: {
  to: string;
  variant?: ButtonVariant;
  className?: string;
  children: ReactNode;
}) {
  const ref = useMagnetic<HTMLAnchorElement>();
  return (
    <Link ref={ref} to={to} className={`${variantClass(variant)} ${className}`}>
      {children}
    </Link>
  );
}

type ButtonProps = ComponentPropsWithoutRef<'button'> & { variant?: ButtonVariant };

export function Button({ variant = 'primary', className = '', type, ...rest }: ButtonProps) {
  return (
    <button type={type ?? 'button'} className={`${variantClass(variant)} ${className}`} {...rest} />
  );
}

export function ArrowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="link-siv group inline-flex min-h-11 items-center gap-2">
      {children}
      <span
        aria-hidden="true"
        className="transition-transform duration-300 ease-[var(--ease-siv)] group-hover:translate-x-1.5 motion-reduce:transition-none"
      >
        →
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Typography                                                          */
/* ------------------------------------------------------------------ */

export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={`eyebrow mb-5 flex items-center gap-2.5 ${className}`}
      style={{ color: 'var(--tone-label)' }}
    >
      <span aria-hidden="true" className="inline-block size-1.5 rotate-45 bg-current" />
      {children}
    </p>
  );
}

export function Display({
  children,
  className = '',
  as: Tag = 'h2',
}: {
  children: ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return <Tag className={`text-[clamp(2rem,4.6vw,3.6rem)] ${className}`}>{children}</Tag>;
}

/**
 * The oversized hero headline, revealed word by word. Takes a plain string so
 * the words can be split without touching markup — the rendered result is still
 * one continuous, selectable sentence.
 */
export function HeroTitle({ children }: { children: string }) {
  return (
    <SplitText
      as="h1"
      text={children}
      className="max-w-[14ch] text-[clamp(2.75rem,8.6vw,5.75rem)] leading-[0.98] tracking-[-0.03em]"
    />
  );
}

export function Lede({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={`mt-6 max-w-[58ch] text-[1.0625rem] leading-[1.75] sm:text-lg ${className}`}
      style={{ color: 'var(--tone-muted)' }}
    >
      {children}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Surfaces                                                            */
/* ------------------------------------------------------------------ */

export function Card({
  children,
  className = '',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'li' | 'article';
}) {
  return <Tag className={`siv-card siv-card-hover p-6 sm:p-7 ${className}`}>{children}</Tag>;
}

/**
 * Navy inset panel for sample output. Carries the `tone-void` variables, so its
 * children resolve their own foreground and stay readable in either theme.
 */
export function InkPanel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`tone-void relative overflow-hidden rounded-2xl border p-6 sm:p-7 ${className}`}
    >
      {children}
    </div>
  );
}

const RUNG_STYLE: Record<string, string> = {
  P1: 'bg-blue-vivid/25 text-accent-2 border-blue-vivid/40',
  P3: 'bg-blue-vivid/25 text-[#9dc0ff] border-blue-vivid/40',
  'P2–P5': 'bg-blue-vivid/25 text-[#9dc0ff] border-blue-vivid/40',
  P6: 'bg-cyan/20 text-accent-2 border-cyan/40',
  'P6–P7': 'bg-cyan/20 text-accent-2 border-cyan/40',
};

/**
 * Evidence rung badge. The rung is carried by the badge *text*, never by colour
 * alone — colour only separates the three tiers.
 */
export function RungBadge({ rung }: { rung: string }) {
  return (
    <span
      className={`font-mono inline-block rounded-md border px-1.5 py-0.5 text-[0.6875rem] font-bold tracking-wider ${
        RUNG_STYLE[rung] ?? 'border-line-strong bg-surface-2 text-text'
      }`}
    >
      {rung}
    </span>
  );
}

/**
 * Wrapper for tables that scroll sideways on narrow screens.
 *
 * A scroll container that is not focusable cannot be scrolled by keyboard at
 * all, so it takes tabindex and a name — the fix axe's
 * `scrollable-region-focusable` rule asks for.
 */
export function ScrollableTable({
  label,
  className = '',
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className={`overflow-x-auto rounded-xl ${className}`}
    >
      {children}
    </div>
  );
}
